export const createReversedMapSet = <Key, Value>() => {
    const main = new Map<Key, Set<Value>>();
    const reversed = new Map<Value, Set<Key>>();
    return Object.freeze({
        add(key: Key, value: Value) {
            const set = main.get(key);
            if (set) {
                set.add(value);
            } else {
                main.set(key, new Set([value]));
            }
            const reversedSet = reversed.get(value);
            if (reversedSet) {
                reversedSet.add(key);
            } else {
                reversed.set(value, new Set([key]));
            }
        },
        remove(key: Key, value: Value) {
            const set = main.get(key);
            if (!set) {
                return false;
            }
            if (set.delete(value)) {
                reversed.get(value)?.delete(key);
                return true;
            }
            return false;
        },
        removeKey(key: Key) {
            const set = main.get(key);
            if (!set) {
                return false;
            }
            for (const value of set) {
                reversed.get(value)?.delete(key);
            }
            return main.delete(key);
        },
        removeReversed(value: Value) {
            const set = reversed.get(value);
            if (!set) {
                return false;
            }
            for (const key of set) {
                main.get(key)?.delete(value);
            }
            return reversed.delete(value);
        },
        size(key: Key) {
            return main.get(key)?.size ?? 0;
        },
        reversedSize(value: Value) {
            return reversed.get(value)?.size ?? 0;
        },
        get(key: Key) {
            return main.get(key);
        },
        getReversed(value: Value) {
            return reversed.get(value);
        },
        has(key: Key, value: Value) {
            return main.get(key)?.has(value) ?? false;
        },
        values() {
            return main.values();
        },
        reverseValues() {
            return reversed.values();
        },
        keys() {
            return main.keys();
        },
        reverseKeys() {
            return reversed.keys();
        },
        entries() {
            return main.entries();
        },
        reverseEntries() {
            return reversed.entries();
        },
        clear() {
            main.clear();
            reversed.clear();
        },
    });
};

export type ReversedMapSet<Key, Value> = ReturnType<
    typeof createReversedMapSet<Key, Value>
>;
